import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Tooltip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { apiRequest } from "../config/api";
import { GearTshirt, GearShorts, GearSocks, GearDesign } from "../types";
import GearDesignPicker from "../components/GearDesignPicker";

interface BankDetails {
  iban: string;
  bic: string;
  accountName: string;
  bankName?: string;
}

interface ConfirmationData {
  id: string;
  playerName: string;
  email: string;
  phone: string;
  isExistingPlayer: boolean;
  assignedDivision?: string;
  projectionType?: "promoted" | "relegated" | "stays";
  wantsTrialDay: boolean;
  trialDayResponse: "yes" | "no" | null;
  wantsGear: boolean;
  gearOption?: string;
  gearTshirts: GearTshirt[];
  gearShorts: GearShorts[];
  gearSocks: GearSocks[];
  gearTotalCost: number;
  paymentReference: string;
  confirmationLocked: boolean;
  confirmationSubmittedAt: Date | null;
  registrationFee: number;
  totalAmount: number;
  bankDetails: BankDetails;
  tshirtPrice: number;
  shortsPrice: number;
  kitPrice: number;
  seasonName: string;
  trialDayDate?: string | null;
  tshirtSizeChartUrl?: string | null;
  poloSizeChartUrl?: string | null;
  shortsSizeChartUrl?: string | null;
}

const SIZES = ["S", "M", "L", "XL", "XXL"] as const;
const SHIRT_TYPES = [
  { value: "tshirt", label: "T-Shirt" },
  { value: "polo", label: "Polo" },
];
const SOCK_SIZES = [
  "35",
  "36",
  "37",
  "38",
  "39",
  "40",
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
  "47",
];

function calcGearTotal(
  shirts: GearTshirt[],
  shorts: GearShorts[],
  socks: GearSocks[],
  tshirtPrice: number,
  shortsPrice: number,
  kitPrice: number,
): number {
  if (shirts.length === 0 && shorts.length === 0 && socks.length === 0)
    return 0;
  if (shirts.length > 0 && shorts.length > 0) {
    return (
      kitPrice +
      Math.max(0, shirts.length - 1) * tshirtPrice +
      Math.max(0, shorts.length - 1) * shortsPrice
    );
  }
  if (shirts.length > 0) return shirts.length * tshirtPrice;
  if (shorts.length > 0) return shorts.length * shortsPrice;
  return 0;
}

export default function SeasonConfirmationPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ConfirmationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // Form state
  const [phone, setPhone] = useState("");
  const [gearTshirts, setGearTshirts] = useState<GearTshirt[]>([]);
  const [gearShorts, setGearShorts] = useState<GearShorts[]>([]);
  const [gearSocks, setGearSocks] = useState<GearSocks[]>([]);
  const [trialDayResponse, setTrialDayResponse] = useState<"yes" | "no" | "">(
    "",
  );
  const [gearDesigns, setGearDesigns] = useState<GearDesign[]>([]);
  const [hadLegacyGearSelection, setHadLegacyGearSelection] = useState(false);
  const tshirtDesigns = gearDesigns.filter(
    (d) => (d.category || "tshirt") === "tshirt",
  );
  const poloDesigns = gearDesigns.filter((d) => d.category === "polo");
  const shortsDesigns = gearDesigns.filter((d) => d.category === "shorts");

  useEffect(() => {
    if (!token) {
      setError("Invalid confirmation link.");
      setLoading(false);
      return;
    }
    Promise.all([
      apiRequest(`/api/season-confirm/${token}`, { method: "GET" }),
      apiRequest("/api/season-registration/gear-designs", { method: "GET" }),
    ])
      .then(async ([res, designsRes]) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(
            body.error ||
              "This confirmation link is not valid or has been disabled.",
          );
          return;
        }
        const d: ConfirmationData = await res.json();
        setData(d);
        setPhone(d.phone || "");
        // Legacy red/white/black `color` selections are no longer valid — a
        // t-shirt is only considered "has a selection" once it carries a
        // designId from the current catalog. Strip the old field so it can't
        // leak back into what we submit, and flag it so we can tell the
        // player their old choice was reset.
        const shirts = d.gearTshirts || [];
        setHadLegacyGearSelection(
          shirts.some((t: any) => t.color && !t.designId),
        );
        setGearTshirts(
          shirts.map(({ color, ...rest }: any) => rest) as GearTshirt[],
        );
        setGearShorts(d.gearShorts || []);
        setGearSocks(d.gearSocks || []);
        setTrialDayResponse(d.trialDayResponse || "");

        if (designsRes.ok) {
          setGearDesigns(await designsRes.json());
        }
      })
      .catch(() =>
        setError("Failed to load your registration. Please try again later."),
      )
      .finally(() => setLoading(false));
  }, [token]);

  const computedGearTotal = data
    ? calcGearTotal(
        gearTshirts,
        gearShorts,
        gearSocks,
        data.tshirtPrice,
        data.shortsPrice,
        data.kitPrice,
      )
    : 0;
  const computedTotal = (data?.registrationFee ?? 110) + computedGearTotal;

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const handleSubmit = async () => {
    if (!data) return;
    if (!phone.trim() || phone.trim().length < 5) {
      setError("Please enter a valid phone number.");
      return;
    }
    if (data.assignedDivision === "Trial Day" && !trialDayResponse) {
      setError("Please confirm whether you will attend the Trial Day.");
      return;
    }
    // Validate t-shirt & shorts/skirt designs
    const missingDesigns =
      gearTshirts.some((t) => !t.designId) || gearShorts.some((s) => !s.designId);
    if (missingDesigns) {
      setError("Please select a design for each t-shirt and shorts/skirt item.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiRequest(`/api/season-confirm/${token}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: phone.trim(),
          gearTshirts,
          gearShorts,
          gearSocks,
          trialDayResponse: trialDayResponse || undefined,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || "Failed to submit confirmation.");
        return;
      }
      setSubmitSuccess(true);
      setData((prev) =>
        prev
          ? {
              ...prev,
              confirmationLocked: true,
              gearTotalCost: body.gearTotalCost,
              totalAmount: body.totalAmount,
            }
          : prev,
      );
    } catch {
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f4f4f4"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f4f4f4"
        p={2}
      >
        <Paper
          sx={{ p: 4, maxWidth: 500, textAlign: "center", borderRadius: 3 }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Link Not Available
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please contact the SP Team for assistance:{" "}
            <strong>Aly, Goncalo, or Jule</strong>.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!data) return null;

  const isLocked = data.confirmationLocked;
  const isTrialDay = data.assignedDivision === "Trial Day";
  const hasMissingDesigns =
    gearTshirts.some((t) => !t.designId) || gearShorts.some((s) => !s.designId);

  return (
    <Box minHeight="100vh" bgcolor="#f4f4f4">
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)",
          py: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h4" color="white" fontWeight="bold">
          Stuttgart Padel League
        </Typography>
        <Typography color="#a0c4ff" mt={1}>
          {data.seasonName}
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {/* Locked success banner */}
        {(isLocked || submitSuccess) && (
          <Alert
            icon={<CheckCircleOutlineIcon />}
            severity="success"
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Typography fontWeight="bold">
              Your registration is confirmed! ✓
            </Typography>
            <Typography variant="body2" mt={0.5}>
              For changes to your gear order or Trial Day RSVP, please contact
              the SP Team: <strong>Aly, Goncalo, or Jule</strong>. Once your
              account is activated, you can manage your profile directly via the
              SPL website.
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Greeting */}
        <Paper sx={{ p: 3, mb: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Welcome, {data.playerName}! 🎾
          </Typography>
          <Typography color="text.secondary">
            We're happy to have you on board for{" "}
            <strong>{data.seasonName}</strong>. Please review the information
            below and confirm your registration.
          </Typography>
        </Paper>

        {/* Division block */}
        {data.assignedDivision && (
          <Paper
            sx={{
              p: 3,
              mb: 2,
              borderRadius: 3,
              borderLeft: "4px solid",
              borderColor: isTrialDay
                ? "#ffc107"
                : data.projectionType === "promoted"
                  ? "#4caf50"
                  : "#9e9e9e",
            }}
          >
            {isTrialDay ? (
              <>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="#856404"
                  gutterBottom
                >
                  🎾 Trial Day Invitation
                </Typography>
                <Typography color="text.secondary">
                  You have been invited to participate in our{" "}
                  <strong>
                    Trial Day{data.trialDayDate ? ` on ${data.trialDayDate}` : ""}
                  </strong>
                  . Please block this time in your calendar — more details will
                  follow.
                </Typography>
                <Typography color="text.secondary" mt={1}>
                  Please confirm your attendance using the radio buttons in the
                  "Trial Day RSVP" section below.
                </Typography>
              </>
            ) : data.projectionType === "promoted" ? (
              <>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="#2e7d32"
                  gutterBottom
                >
                  🏆 Congratulations — Promoted!
                </Typography>
                <Typography color="text.secondary">
                  Based on your outstanding performance this season, you have
                  been <strong>promoted</strong> to{" "}
                  <strong>{data.assignedDivision}</strong>! Well deserved! 🎾
                </Typography>
              </>
            ) : !data.isExistingPlayer ? (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  🎉 Welcome to the SP Community!
                </Typography>
                <Typography color="text.secondary">
                  We are thrilled to have you join us! You will be playing in{" "}
                  <strong>{data.assignedDivision}</strong> next season.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  📋 Your Division
                </Typography>
                <Typography color="text.secondary">
                  You will be playing in{" "}
                  <strong>{data.assignedDivision}</strong> next season.
                </Typography>
              </>
            )}
            {data.wantsTrialDay && !isTrialDay && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                We received your interest in the Trial Day — thank you!
                Unfortunately, due to very limited spots reserved for new
                players we haven't seen play before, we were unable to include
                you this time. We sincerely apologise. 🙏
              </Alert>
            )}
          </Paper>
        )}

        {/* Phone number */}
        <Paper sx={{ p: 3, mb: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            📱 WhatsApp / Phone Number
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Your phone number will be used to add you to the SPL WhatsApp group.
          </Typography>
          <TextField
            fullWidth
            label="Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={isLocked}
            placeholder="+49 123 456 7890"
          />
        </Paper>

        {/* Gear order */}
        <Paper sx={{ p: 3, mb: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            👕 Gear Order
          </Typography>

          {isLocked ? (
            gearTshirts.length === 0 &&
            gearShorts.length === 0 &&
            gearSocks.length === 0 ? (
              <Typography color="text.secondary">No gear ordered.</Typography>
            ) : (
              <Stack spacing={1}>
                {gearTshirts.map((t, i) => (
                  <Chip
                    key={i}
                    label={`${t.shirtType === "polo" ? "Polo" : "T-Shirt"} – ${t.designName || "No design selected"} – Size ${t.size}${t.nameOnShirt ? ` – "${t.nameOnShirt}"` : ""}`}
                    variant="outlined"
                  />
                ))}
                {gearShorts.map((s, i) => (
                  <Chip
                    key={i}
                    label={`Shorts/Skirt – ${s.designName || "No design selected"} – Size ${s.size}`}
                    variant="outlined"
                  />
                ))}
                {gearSocks.map((s, i) => (
                  <Chip
                    key={i}
                    label={`Socks – Shoe size ${s.size}`}
                    variant="outlined"
                  />
                ))}
              </Stack>
            )
          ) : (
            <>
              {hadLegacyGearSelection && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  🎨 <strong>We now have real t-shirt &amp; shorts/skirt designs!</strong>{" "}
                  Your previous color choice has been cleared — please pick your
                  favorite design below for each item.
                </Alert>
              )}

              {/* T-Shirts */}
              <Box mb={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    T-Shirts / Polo
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() =>
                      setGearTshirts([
                        ...gearTshirts,
                        {
                          size: "M",
                          shirtType: "tshirt",
                          nameOnShirt: "",
                        },
                      ])
                    }
                  >
                    Add
                  </Button>
                </Box>
                {gearTshirts.map((shirt, i) => (
                  <Box
                    key={i}
                    mb={2}
                    p={1.5}
                    borderRadius={1}
                    border="1px solid"
                    borderColor="divider"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={shirt.shirtType}
                          label="Type"
                          onChange={(e) => {
                            const newType = e.target.value as any;
                            setGearTshirts(
                              gearTshirts.map((s, idx) =>
                                idx === i
                                  ? {
                                      ...s,
                                      shirtType: newType,
                                      // The design catalog is split by shirt
                                      // type, so a design picked under one
                                      // never carries over as valid under
                                      // the other.
                                      designId: undefined,
                                      designName: undefined,
                                    }
                                  : s,
                              ),
                            );
                          }}
                        >
                          {SHIRT_TYPES.map((st) => (
                            <MenuItem key={st.value} value={st.value}>
                              {st.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setGearTshirts(
                            gearTshirts.filter((_, idx) => idx !== i),
                          )
                        }
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Box>
                    <GearDesignPicker
                      designs={
                        shirt.shirtType === "polo" ? poloDesigns : tshirtDesigns
                      }
                      selectedId={shirt.designId}
                      onSelect={(designId) => {
                        const design = (
                          shirt.shirtType === "polo" ? poloDesigns : tshirtDesigns
                        ).find((d) => d.id === designId);
                        setGearTshirts(
                          gearTshirts.map((s, idx) =>
                            idx === i
                              ? { ...s, designId: design?.id, designName: design?.name }
                              : s,
                          ),
                        );
                      }}
                      label="Design"
                      sizeChartUrl={
                        shirt.shirtType === "polo"
                          ? data?.poloSizeChartUrl || undefined
                          : data?.tshirtSizeChartUrl || undefined
                      }
                    />
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <InputLabel>Size</InputLabel>
                        <Select
                          value={shirt.size}
                          label="Size"
                          onChange={(e) =>
                            setGearTshirts(
                              gearTshirts.map((s, idx) =>
                                idx === i
                                  ? { ...s, size: e.target.value as any }
                                  : s,
                              ),
                            )
                          }
                        >
                          {SIZES.map((sz) => (
                            <MenuItem key={sz} value={sz}>
                              {sz}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Name on shirt (optional)"
                        value={shirt.nameOnShirt || ""}
                        onChange={(e) =>
                          setGearTshirts(
                            gearTshirts.map((s, idx) =>
                              idx === i
                                ? { ...s, nameOnShirt: e.target.value }
                                : s,
                            ),
                          )
                        }
                        sx={{ flex: 1, minWidth: 150 }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Shorts/Skirt */}
              <Box mb={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Shorts/Skirt
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => {
                      setGearShorts([...gearShorts, { size: "M" }]);
                      setGearSocks([...gearSocks, { size: "42" }]);
                    }}
                  >
                    Add
                  </Button>
                </Box>
                {gearShorts.map((s, i) => (
                  <Box
                    key={i}
                    mb={2}
                    p={1.5}
                    borderRadius={1}
                    border="1px solid"
                    borderColor="divider"
                  >
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      mb={0.5}
                    >
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setGearShorts(gearShorts.filter((_, idx) => idx !== i))
                        }
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Box>
                    <GearDesignPicker
                      designs={shortsDesigns}
                      selectedId={s.designId}
                      onSelect={(designId) => {
                        const design = shortsDesigns.find((d) => d.id === designId);
                        setGearShorts(
                          gearShorts.map((sh, idx) =>
                            idx === i
                              ? { ...sh, designId: design?.id, designName: design?.name }
                              : sh,
                          ),
                        );
                      }}
                      label="Design"
                      sizeChartUrl={data?.shortsSizeChartUrl || undefined}
                    />
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Size</InputLabel>
                      <Select
                        value={s.size}
                        label="Size"
                        onChange={(e) =>
                          setGearShorts(
                            gearShorts.map((sh, idx) =>
                              idx === i
                                ? { ...sh, size: e.target.value as any }
                                : sh,
                            ),
                          )
                        }
                      >
                        {SIZES.map((sz) => (
                          <MenuItem key={sz} value={sz}>
                            {sz}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))}
              </Box>

              {/* Socks */}
              <Box mb={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Socks (auto-added with shorts)
                  </Typography>
                </Box>
                {gearSocks.map((s, i) => (
                  <Box
                    key={i}
                    display="flex"
                    gap={1}
                    mb={1}
                    alignItems="center"
                  >
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Shoe size</InputLabel>
                      <Select
                        value={s.size}
                        label="Shoe size"
                        onChange={(e) =>
                          setGearSocks(
                            gearSocks.map((sk, idx) =>
                              idx === i ? { ...sk, size: e.target.value } : sk,
                            ),
                          )
                        }
                      >
                        {SOCK_SIZES.map((sz) => (
                          <MenuItem key={sz} value={sz}>
                            {sz}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setGearSocks(gearSocks.filter((_, idx) => idx !== i))
                      }
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              {/* Kit pricing hint */}
              {gearTshirts.length > 0 && gearShorts.length > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  First shirt + shorts pair is billed as a full kit (€
                  {data.kitPrice}). Additional items are priced individually.
                </Alert>
              )}

              <Box display="flex" justifyContent="flex-end">
                <Typography variant="body2" color="text.secondary">
                  Gear subtotal:{" "}
                  <strong>€{computedGearTotal.toFixed(2)}</strong>
                </Typography>
              </Box>
            </>
          )}
        </Paper>

        {/* Trial Day RSVP */}
        {isTrialDay && (
          <Paper
            sx={{
              p: 3,
              mb: 2,
              borderRadius: 3,
              borderLeft: "4px solid #ffc107",
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              🎾 Trial Day RSVP
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              The Trial Day is planned{" "}
              {data.trialDayDate ? (
                <>
                  for <strong>{data.trialDayDate}</strong>
                </>
              ) : (
                "soon"
              )}
              . Please book this slot in your calendar.
            </Typography>
            {isLocked ? (
              <Chip
                label={
                  data.trialDayResponse === "yes"
                    ? "✓ Attending"
                    : data.trialDayResponse === "no"
                      ? "✗ Not Attending"
                      : "Not answered"
                }
                color={data.trialDayResponse === "yes" ? "success" : "default"}
                variant="outlined"
              />
            ) : (
              <>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant={
                      trialDayResponse === "yes" ? "contained" : "outlined"
                    }
                    color="success"
                    onClick={() => setTrialDayResponse("yes")}
                  >
                    ✓ Yes, I will attend
                  </Button>
                  <Button
                    variant={
                      trialDayResponse === "no" ? "contained" : "outlined"
                    }
                    color="error"
                    onClick={() => setTrialDayResponse("no")}
                  >
                    ✗ I cannot attend
                  </Button>
                </Stack>
                {trialDayResponse === "no" && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Initial Placement & Evaluation
                    </Typography>
                    <Typography variant="body2">
                      If you cannot attend the Trial Day, you will be initially
                      placed in Division 3 or 4. Don't worry — your first 2
                      league days will be used to evaluate your level, and we
                      will move you up or down to ensure you're in the right
                      division for balanced, competitive play.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </Paper>
        )}

        {/* Bank details + payment */}
        <Paper
          sx={{ p: 3, mb: 2, borderRadius: 3, border: "2px solid #d32f2f" }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            💳 Payment Details
          </Typography>

          {/* Urgent payment notice */}
          <Box
            sx={{
              background: "#d32f2f",
              borderRadius: 2,
              p: 2,
              mb: 2.5,
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <Typography variant="h5" lineHeight={1}>
              ⚠️
            </Typography>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="#fff"
                gutterBottom
              >
                Action required: Transfer your payment NOW
              </Typography>
              <Typography variant="body2" sx={{ color: "#ffcdd2" }}>
                Your registration is{" "}
                <strong style={{ color: "#fff" }}>
                  only confirmed once we receive your payment
                </strong>
                . Please complete the bank transfer immediately after submitting
                this form — your spot will not be reserved until payment is
                received.
              </Typography>
            </Box>
          </Box>

          {/* Bank info */}
          <Box sx={{ background: "#f5f5f5", borderRadius: 2, p: 2, mb: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="text.secondary"
              gutterBottom
            >
              Bank Transfer Details
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>Account Name:</strong> {data.bankDetails.accountName}
              </Typography>
              {data.bankDetails.bankName && (
                <Typography variant="body2">
                  <strong>Bank:</strong> {data.bankDetails.bankName}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>IBAN:</strong> {data.bankDetails.iban || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>BIC:</strong> {data.bankDetails.bic || "—"}
              </Typography>
            </Stack>
          </Box>

          {/* Verwendungszweck */}
          <Box sx={{ background: "#e3f2fd", borderRadius: 2, p: 2, mb: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="#1565c0"
              gutterBottom
            >
              🏦 Payment Reference (Verwendungszweck)
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Please use this exact reference when making your bank transfer so
              we can identify your payment.
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  background: "#fff",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  border: "1px solid #90caf9",
                  flex: 1,
                  wordBreak: "break-all",
                }}
              >
                {data.paymentReference || "—"}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy"}>
                <IconButton
                  onClick={() => handleCopy(data.paymentReference || "")}
                  size="small"
                  color={copied ? "success" : "default"}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Cost breakdown */}
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={0.5}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Season Registration Fee
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                €{(data.registrationFee || 110).toFixed(2)}
              </Typography>
            </Box>
            {computedGearTotal > 0 && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Gear Order
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  €{computedGearTotal.toFixed(2)}
                </Typography>
              </Box>
            )}
            <Divider />
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight="bold" fontSize="1.1rem">
                Total
              </Typography>
              <Typography fontWeight="bold" fontSize="1.1rem" color="#1a1a2e">
                €{computedTotal.toFixed(2)}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* New player note */}
        {!data.isExistingPlayer && (
          <Paper
            sx={{
              p: 3,
              mb: 2,
              borderRadius: 3,
              borderLeft: "4px solid #9c27b0",
              background: "#f3e5f5",
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="#4a148c"
              gutterBottom
            >
              🔐 Account Activation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Once our team confirms your payment, your SPL account will be
              activated and you will be able to log in to the SPL website. You
              will receive an automated email at that point.
            </Typography>
          </Paper>
        )}

        {/* Submit / Locked */}
        {!isLocked && (
          <Box textAlign="center" mt={3} mb={4}>
            {hasMissingDesigns && (
              <Alert
                severity="warning"
                sx={{ mb: 2, maxWidth: 600, mx: "auto" }}
              >
                ⚠️ Please select a design for each t-shirt and shorts/skirt item
                before confirming your registration.
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              disabled={submitting || hasMissingDesigns}
              onClick={handleSubmit}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LockOutlinedIcon />
                )
              }
              sx={{
                background: "#1a1a2e",
                "&:hover": { background: "#0f3460" },
                px: 5,
                py: 1.5,
                borderRadius: 2,
                fontSize: "1rem",
              }}
            >
              {submitting ? "Confirming..." : "Confirm & Lock Registration"}
            </Button>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              mt={1}
            >
              Once confirmed, your registration is final and this page cannot be
              reused.
            </Typography>
          </Box>
        )}

        {/* Footer */}
        <Box textAlign="center" pb={4}>
          <Typography variant="caption" color="text.disabled">
            Stuttgart Padel League · {data.seasonName} · {data.email}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
